import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";

/* ──────────────────────────────────────────────────────────────
   Config
────────────────────────────────────────────────────────────── */
const API_BASE = "https://api.gabrielny.com:8080/ai_dashboard.php";
const API_GET = `${API_BASE}?query=natquery_all`;

const UI = {
  purple: "#601EF9",
  purpleDark: "#7A3FFF",
  indigo600: "#4F46E5",
  slate600: "#4B5563",
  slate700: "#374151",
  chipBg: "#EEF2FF",
  chipBorder: "#E0E7FF",
  chipText: "#4338CA",
};

const MAP_LABELS = {
  attCategory: "Category",
  attCollection: "Collection",
  itemGrade: "Grade",
  attMaterial: "Material",
  attMetal: "Metal",
  attOccasion: "Occasion",
  attStyle: "Style",
  attDiamondQuality: "Diamond Quality",
  attDivision: "Division",
  attStoneColorQuality: "Stone Color Quality",
  attBandWeight: "Band Weight",
};

const MAPPING_OPTIONS = Object.entries(MAP_LABELS).map(([key, label]) => ({
  key,
  label,
}));

/* ──────────────────────────────────────────────────────────────
   Helpers
────────────────────────────────────────────────────────────── */
async function postQuery(payload) {
  const res = await fetch(API_BASE, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
  try { return await res.json(); } catch { return {}; }
}

function humanizeKey(k = "") {
  const w = k.startsWith("att") ? k.slice(3) : k;
  const spaced = w.replace(/([A-Z])/g, " $1").trim();
  return spaced ? spaced[0].toUpperCase() + spaced.slice(1) : k;
}

/** Normalize commas (used on blur and just before submit) */
function normalizeCommaList(s = "") {
  return String(s)
    .replace(/\s*,\s*/g, ",")  // trim around commas
    .replace(/,+/g, ",")       // collapse duplicate commas
    .replace(/^,|,$/g, "");    // remove leading/trailing comma (finalize)
}

/** Split but **do not** penalize trailing comma while typing */
function splitByCommasLoose(s = "") {
  return String(s)
    .split(",")
    .map((t) => t.trim())
    .filter((t) => t.length > 0);
}

/** Parse "key: [a,b] AND key2: [x,y]" */
function parseCompositeRuleValue(raw) {
  if (!raw) return [];
  const cleaned = String(raw).replace(/\\/g, "").trim();
  const parts = cleaned.split(/\s+(AND|OR)\s+/i);
  const clauses = [];
  let currentOp = null;

  for (let i = 0; i < parts.length; i++) {
    const token = parts[i]?.trim();
    if (!token) continue;
    if (/^(AND|OR)$/i.test(token)) { currentOp = token.toUpperCase(); continue; }

    const m = token.match(/^([A-Za-z_][\w]*)\s*:\s*(.+)$/);
    if (!m) { clauses.push({ op: currentOp, key: "", values: [token] }); currentOp = null; continue; }

    const key = m[1];
    let val = m[2].trim();
    if (val.startsWith("[") && val.endsWith("]")) val = val.slice(1, -1).trim();
    const values = splitByCommasLoose(val);
    clauses.push({ op: currentOp, key, values });
    currentOp = null;
  }

  if (clauses.length === 0) {
    const m = cleaned.match(/^([A-Za-z_][\w]*)\s*:\s*(.+)$/);
    if (m) {
      let v = m[2].trim();
      if (v.startsWith("[") && v.endsWith("]")) v = v.slice(1, -1).trim();
      const values = splitByCommasLoose(v);
      return [{ op: null, key: m[1], values }];
    }
  }
  return clauses;
}

/** Serialize to "key: [a, b] AND key2: [x]" */
function formatCompositeRuleValue(clauses = []) {
  const segs = [];
  clauses.forEach((c, idx) => {
    const vs = (c.values || []).filter(Boolean);
    const body = c.key
      ? vs.length > 1 ? `${c.key}: [${vs.join(", ")}]` : `${c.key}: ${vs[0] ?? ""}`
      : (vs[0] ?? "");
    if (idx === 0 || !c.op) segs.push(body); else segs.push(`${c.op} ${body}`);
  });
  return segs.join(" ");
}

/* ──────────────────────────────────────────────────────────────
   Icons
────────────────────────────────────────────────────────────── */
const PenIcon = (props) => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" {...props}>
    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zm18-11.06c0 .4-.16.78-.44 1.06l-2.12 2.12-3.75-3.75 2.12-2.12a1.5 1.5 0 0 1 2.12 0l1.63 1.63c.28.28.44.66.44 1.06z"/>
  </svg>
);
const TrashIcon = (props) => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" {...props}>
    <path d="M9 3h6a1 1 0 0 1 1 1v1h5v2H3V5h5V4a1 1 0 0 1 1-1zm-3 6h12l-1 12a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L6 9z"/>
  </svg>
);

/* ──────────────────────────────────────────────────────────────
   Modal (scrollable)
────────────────────────────────────────────────────────────── */
function Modal({ open, title, onRequestClose, children, maxWidth = 560 }) {
  if (!open) return null;
  return (
    <div role="dialog" aria-modal="true" onClick={onRequestClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex",
               alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 12 }}>
      <div onClick={(e)=>e.stopPropagation()}
        style={{ background: "#fff", width: "100%", maxWidth, borderRadius: 12,
                 boxShadow: "0 10px 30px rgba(0,0,0,0.2)", fontFamily: "inherit",
                 maxHeight: "85vh", display: "flex", flexDirection: "column" }}>
        {title ? <h3 style={{ margin: "16px 20px 8px", color: UI.slate700, flex: "0 0 auto" }}>{title}</h3> : null}
        <div style={{ padding: 20, overflowY: "auto", flex: "1 1 auto" }}>{children}</div>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────
   Clause editor — trailing comma stays while typing
────────────────────────────────────────────────────────────── */
function ClauseEditor({ idx, clause, onChange, onRemove }) {
  const [valuesText, setValuesText] = useState((clause.values || []).join(", "));

  // Re-sync text if the clause key changes (or on mount)
  useEffect(() => {
    setValuesText((clause.values || []).join(", "));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clause.key]);

  const handleTextChange = (e) => {
    const raw = e.target.value;
    setValuesText(raw);                      // keep trailing comma visible
    const parsed = splitByCommasLoose(raw);  // keep values state in sync
    onChange({ ...clause, values: parsed });
  };

  const handleBlur = (e) => {
    const normalized = normalizeCommaList(e.target.value);
    setValuesText(normalized);
    onChange({ ...clause, values: splitByCommasLoose(normalized) });
  };

  return (
    <div style={{ display: "grid", gap: 8, border: "1px solid #E5E7EB", borderRadius: 10, padding: 12 }}>
      {idx > 0 && (
        <label>
          Join with
          <select
            value={clause.op || "AND"}
            onChange={(e) => onChange({ ...clause, op: e.target.value })}
            style={selectStyle()}
          >
            <option value="AND">AND</option>
            <option value="OR">OR</option>
          </select>
        </label>
      )}

      <label>
        Mapping Type
        <select
          value={clause.key}
          onChange={(e) => onChange({ ...clause, key: e.target.value })}
          required
          style={selectStyle()}
        >
          <option value="" disabled>Select…</option>
          {MAPPING_OPTIONS.map((opt) => (
            <option key={opt.key} value={opt.key}>{opt.label}</option>
          ))}
        </select>
      </label>

      <label>
        Values (<b>comma-separated</b>)
        <input
          value={valuesText}
          onChange={handleTextChange}
          onBlur={handleBlur}
          placeholder="e.g. AN, ER, WB"
          required
          style={inputStyle()}
        />
      </label>

      {idx > 0 && (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button type="button" onClick={onRemove} style={dangerBtnStyle()}>Remove clause</button>
        </div>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────
   Modals
────────────────────────────────────────────────────────────── */
function AddModal({ open, onClose, onSuccess }) {
  const [form, setForm] = useState({ word: "", clauses: [{ op: null, key: "", values: [] }] });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const setClauseAt = (i, next) =>
    setForm(f => { const clauses = f.clauses.slice(); clauses[i] = next; return { ...f, clauses }; });
  const addClause = (op = "AND") =>
    setForm(f => ({ ...f, clauses: [...f.clauses, { op, key: "", values: [] }] }));
  const removeClause = (i) =>
    setForm(f => ({ ...f, clauses: f.clauses.filter((_, idx) => idx !== i) }));

  // ✅ Simpler validation: need a word, and each clause needs a key + ≥1 value
  const canSubmit =
    form.word.trim().length > 0 &&
    form.clauses.every(c => c.key && (c.values?.length || 0) > 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    // Finalize normalization right before submit (even if user didn't blur)
    const finalizedClauses = form.clauses.map(c => ({
      ...c,
      values: splitByCommasLoose(normalizeCommaList((c.values || []).join(","))),
    }));

    setSubmitting(true); setError(""); setResult(null);
    try {
      const ruleValue = formatCompositeRuleValue(finalizedClauses);
      const data = await postQuery({ query: "natquery_save", ruleKey: form.word, ruleValue, user_id: 101 });
      setResult(data);
      onSuccess && onSuccess(data);
    } catch (err) {
      setError(err.message || "Request failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onRequestClose={onClose} title="">
      <form onSubmit={submit} style={{ display: "grid", gap: 12 }}>
        <label>
          Word
          <input
            value={form.word}
            onChange={(e)=>setForm(f=>({...f, word: e.target.value}))}
            required
            style={inputStyle()}
          />
        </label>

        <div style={{ display: "grid", gap: 10, maxHeight: "50vh", overflowY: "auto", paddingRight: 4 }}>
          {form.clauses.map((c, i) => (
            <ClauseEditor key={i} idx={i} clause={c}
              onChange={(next)=>setClauseAt(i,next)}
              onRemove={()=>removeClause(i)} />
          ))}
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button type="button" onClick={()=>addClause("AND")} style={chipBtnStyle()}>+ Add AND Clause</button>
          <button type="button" onClick={()=>addClause("OR")} style={chipBtnStyle()}>+ Add OR Clause</button>
        </div>

        {error && <div style={{ color: "crimson" }}>{error}</div>}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button type="button" onClick={onClose} disabled={submitting} style={ghostBtnStyle()}>Cancel</button>
          <button type="submit" disabled={!canSubmit || submitting} style={primaryBtnStyle(submitting)}>
            {submitting ? "Submitting..." : "Submit"}
          </button>
        </div>

        {result && <pre style={{ background: "#f7f7f7", padding: 12, marginTop: 8 }}>{JSON.stringify(result, null, 2)}</pre>}
      </form>
    </Modal>
  );
}

function EditModal({ row, onClose, onSaved }) {
  const open = !!row;
  const [form, setForm] = useState({ ruleKey: "", clauses: [{ op: null, key: "", values: [] }] });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(()=>{
    if (row) {
      const clauses = parseCompositeRuleValue(row.ruleValue ?? "");
      setForm({
        ruleKey: row.ruleKey ?? "",
        clauses: clauses.length ? clauses : [{ op: null, key: "", values: [] }],
      });
    }
  },[row]);

  const setClauseAt = (i, next) =>
    setForm(f => { const clauses = f.clauses.slice(); clauses[i] = next; return { ...f, clauses }; });

  const addClause = (op = "AND") =>
    setForm(f => ({ ...f, clauses: [...f.clauses, { op, key: "", values: [] }] }));

  const removeClause = (i) =>
    setForm(f => ({ ...f, clauses: f.clauses.filter((_, idx) => idx !== i) }));

  const canSubmit =
    form.ruleKey.trim().length > 0 &&
    form.clauses.every(c => c.key && (c.values?.length || 0) > 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!row?.id || !canSubmit) return;

    // finalize values even if user didn’t blur
    const finalizedClauses = form.clauses.map(c => ({
      ...c,
      values: splitByCommasLoose(normalizeCommaList((c.values || []).join(","))),
    }));

    setSubmitting(true); setError("");
    try {
      const ruleValue = formatCompositeRuleValue(finalizedClauses);
      await postQuery({
        query: "natquery_save",
        id: row.id,
        ruleKey: form.ruleKey,
        ruleValue,
        user_id: 101,
      });
      onSaved && onSaved();
    } catch (err) {
      setError(err.message || "Request failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onRequestClose={onClose} title="">
      <form onSubmit={submit} style={{ display: "grid", gap: 12 }}>
        <label>
          Word
          <input
            value={form.ruleKey}
            onChange={(e)=>setForm(f=>({...f, ruleKey: e.target.value}))}
            required
            style={inputStyle()}
          />
        </label>

        {/* Clauses (scrollable) */}
        <div style={{ display: "grid", gap: 10, maxHeight: "50vh", overflowY: "auto", paddingRight: 4 }}>
          {form.clauses.map((c, i) => (
            <ClauseEditor
              key={i}
              idx={i}
              clause={c}
              onChange={(next)=>setClauseAt(i,next)}
              onRemove={()=>removeClause(i)}
            />
          ))}
        </div>

        {/* NEW: Add AND/OR clause buttons */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button type="button" onClick={()=>addClause("AND")} style={chipBtnStyle()}>
            + Add AND Clause
          </button>
          <button type="button" onClick={()=>addClause("OR")} style={chipBtnStyle()}>
            + Add OR Clause
          </button>
        </div>

        {error && <div style={{ color: "crimson" }}>{error}</div>}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button type="button" onClick={onClose} disabled={submitting} style={ghostBtnStyle()}>
            Cancel
          </button>
          <button type="submit" disabled={!canSubmit || submitting} style={primaryBtnStyle(submitting)}>
            {submitting ? "Updating..." : "Update"}
          </button>
        </div>
      </form>
    </Modal>
  );
}


function DeleteModal({ row, onClose, onDeleted }) {
  const open = !!row;
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const onDelete = async () => {
    if (!row?.id) return;
    setSubmitting(true); setError("");
    try { await postQuery({ query: "natquery_delete", id: row.id }); onDeleted && onDeleted(); }
    catch (err) { setError(err.message || "Request failed"); }
    finally { setSubmitting(false); }
  };

  return (
    <Modal open={open} onRequestClose={onClose} title="Delete Mapping">
      <p style={{ marginTop: 0 }}>Are you sure you want to delete <strong>{row?.ruleKey}</strong>?</p>
      {error && <div style={{ color: "crimson", margin: "10px 0" }}>{error}</div>}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button type="button" onClick={onClose} disabled={submitting} style={ghostBtnStyle()}>Cancel</button>
        <button type="button" onClick={onDelete} disabled={submitting} style={dangerBtnStyle()}>
          {submitting ? "Deleting..." : "Delete"}
        </button>
      </div>
    </Modal>
  );
}

/* ──────────────────────────────────────────────────────────────
   Table + Page
────────────────────────────────────────────────────────────── */
const columnHelper = createColumnHelper();

export default function Dictionary() {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const searchRef = useRef(null);

  const [showAdd, setShowAdd] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [deletingRow, setDeletingRow] = useState(null);

  const [sorting, setSorting] = useState([{ id: "ruleKey", desc: false }]);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const fetchData = async () => {
    try {
      const res = await fetch(API_GET);
      const json = await res.json();
      setData(json); setPageIndex(0);
    } catch (e) { console.error("Error fetching data:", e); }
  };

  useEffect(()=>{ fetchData(); }, []);
  useEffect(()=>{ const t = setTimeout(()=> setDebounced(search.trim().toLowerCase()), 300); return ()=> clearTimeout(t); },[search]);

  useEffect(()=> {
    const handler = (e) => {
      if (e.keyCode === 114 || (e.ctrlKey && e.key.toLowerCase() === "f")) {
        e.preventDefault(); searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handler);
    return ()=> window.removeEventListener("keydown", handler);
  },[]);

  const rows = useMemo(()=>{
    const arr = Array.isArray(data) ? data : data ? [data] : [];
    return arr.filter(r => Number(r?.ruleType) === 0);
  },[data]);

  const filtered = useMemo(()=>{
    if (!debounced) return rows;
    return rows.filter(r=>{
      const a = `${r?.ruleKey ?? ""}`.toLowerCase();
      const clauses = parseCompositeRuleValue(r?.ruleValue ?? "");
      const hay = clauses.flatMap(c=>{
        const label = c.key ? (MAP_LABELS[c.key] || humanizeKey(c.key)) : "";
        return [c.key, label, ...(c.values || [])];
      }).join(" ").toLowerCase();
      return a.includes(debounced) || hay.includes(debounced);
    });
  },[rows, debounced]);

  const Highlight = ({ text, query }) => {
    if (!query) return <>{text}</>;
    const q = query.toLowerCase(); const s = String(text ?? ""); const out = [];
    let i = 0;
    for(;;){
      const idx = s.toLowerCase().indexOf(q, i);
      if (idx === -1) { out.push(s.slice(i)); break; }
      out.push(s.slice(i, idx)); out.push(<mark key={idx}>{s.slice(idx, idx+q.length)}</mark>);
      i = idx + q.length;
    }
    return <>{out}</>;
  };

  const columns = useMemo(()=>[
    columnHelper.accessor("ruleKey", {
      id: "ruleKey",
      header: () => <span style={{ fontSize: 14, fontWeight: 700, color: UI.slate600, paddingLeft: 10 }}>Word</span>,
      cell: (info) => <span style={{ fontSize: 14, fontWeight: 600, paddingLeft: 10 }}>
        <Highlight text={info.getValue()} query={debounced} />
      </span>,
    }),
    columnHelper.accessor("ruleValue", {
      id: "ruleValue",
      header: () => <span style={{ fontSize: 14, fontWeight: 700, color: UI.slate600 }}>Mapping</span>,
      cell: (info) => {
        const clauses = parseCompositeRuleValue(info.getValue());
        if (!clauses.length) return <span style={{ color: "#6B7280" }}>—</span>;
        return (
          <div style={{ fontSize: 14, display: "grid", gap: 10 }}>
            {clauses.map((c, i) => {
              const label = c.key ? (MAP_LABELS[c.key] || humanizeKey(c.key)) : "Mapping";
              return (
                <div key={i} style={{ display: "grid", gap: 6 }}>
                  {i > 0 && <div style={{ fontSize: 12, fontWeight: 700, color: "#6B7280" }}>{c.op}</div>}
                  <div style={{ fontWeight: 700, color: UI.slate700 }}>{label}</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {c.values?.length ? c.values.map((v, j)=>(
                      <span key={j}
                        style={{ display: "inline-block", padding: "2px 8px", borderRadius: 999,
                                 background: UI.chipBg, border: `1px solid ${UI.chipBorder}`,
                                 fontSize: 12, fontWeight: 600, color: UI.chipText }}>
                        {v}
                      </span>
                    )) : <span style={{ color: "#6B7280" }}>—</span>}
                  </div>
                </div>
              );
            })}
          </div>
        );
      },
    }),
    columnHelper.display({
      id: "actions",
      header: () => <span style={{ fontSize: 14, fontWeight: 700, color: UI.slate600 }}>Actions</span>,
      cell: (info) => {
        const row = info.row.original; const disabled = !row?.id;
        return (
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button title="Edit mapping" onClick={()=>!disabled && setEditingRow(row)} disabled={disabled} style={iconBtnStyle()}>
              <PenIcon />
            </button>
            <button title="Delete mapping" onClick={()=>!disabled && setDeletingRow(row)} disabled={disabled}
              style={{ ...iconBtnStyle(), borderColor: "#FCA5A5", color: "#B91C1C", background: "#FEF2F2" }}>
              <TrashIcon />
            </button>
          </div>
        );
      },
    }),
  ],[debounced]);

  const table = useReactTable({
    data: filtered,
    columns,
    state: { sorting, pagination: { pageIndex, pageSize } },
    onSortingChange: setSorting,
    onPaginationChange: (updater) => {
      const next = typeof updater === "function" ? updater({ pageIndex, pageSize }) : updater;
      setPageIndex(next.pageIndex); setPageSize(next.pageSize);
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const total = filtered.length;
  const start = total ? pageIndex * pageSize + 1 : 0;
  const end = Math.min((pageIndex + 1) * pageSize, total);

  return (
    <div style={{ fontFamily: "Arial", padding: 16 }}>
      {/* Add */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
        <button
          onClick={() => setShowAdd(true)}
          style={primaryBtnStyle(false)}
          onMouseEnter={(e) => (e.currentTarget.style.background = UI.purpleDark)}
          onMouseLeave={(e) => (e.currentTarget.style.background = UI.purple)}
        >
          ＋ Add Mapping
        </button>
      </div>

      {/* Search */}
      <div style={{ padding: 8 }}>
        <input
          ref={searchRef}
          style={{ width: "100%", padding: 12, background: "#F9FAFB", borderRadius: 8, border: `1px solid ${UI.indigo600}` }}
          type="text"
          placeholder="Search word, type, or value… (Ctrl+F)"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPageIndex(0); }}
        />
      </div>

      {/* Counter */}
      <div style={{ marginBottom: 8, fontSize: 12, color: UI.slate600 }}>
        Showing <strong>{start}-{end}</strong> of <strong>{total}</strong>
      </div>

      {/* Table */}
      <div style={{ marginTop: 12, overflowX: "auto" }}>
        <table style={{ width: "100%" }}>
          <thead>
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id} style={{ borderBottom: "2px solid #E5E7EB" }}>
                {hg.headers.map((header) => {
                  const isSorted = header.column.getIsSorted();
                  const sortable = header.id !== "actions";
                  return (
                    <th key={header.id} colSpan={header.colSpan}
                        onClick={sortable ? header.column.getToggleSortingHandler() : undefined}
                        style={{ cursor: sortable ? "pointer" : "default", userSelect: "none",
                                 borderBottom: "1px solid #E5E7EB", paddingTop: 16, paddingBottom: 8, paddingRight: 16, textAlign: "left" }}
                        title={sortable ? "Click to sort" : undefined}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14 }}>
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {sortable && <span style={{ fontSize: 12 }}>{isSorted === "asc" ? "▲" : isSorted === "desc" ? "▼" : ""}</span>}
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row, i) => (
              <tr key={row.id} style={{ background: i % 2 ? "#F9FAFB" : "white" }}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} style={{ minWidth: 180, paddingTop: 12, paddingBottom: 12, paddingRight: 160, verticalAlign: "top" }}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
            {table.getRowModel().rows.length === 0 && (
              <tr>
                <td colSpan={columns.length} style={{ padding: 24, textAlign: "center", fontSize: 14, color: "#6B7280" }}>
                  {data ? "No results match your search." : "Loading..."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "space-between", alignItems: "center", width: "100%", marginTop: 12 }}>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => table.firstPage()} disabled={!table.getCanPreviousPage()}>« First</button>
          <button onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>‹ Prev</button>
        </div>
        <span style={{ fontSize: 14 }}>Page <strong>{pageIndex + 1}</strong> / {table.getPageCount() || 1}</span>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 14 }}>Go to:</span>
          <input
            type="number" min={1}
            value={pageIndex + 1}
            onChange={(e) => { const page = e.target.value ? Number(e.target.value) - 1 : 0; table.setPageIndex(page); }}
            style={{ border: "1px solid #E5E7EB", borderRadius: 6, width: 64, textAlign: "center", padding: "4px 6px" }}
          />
          <select
            value={pageSize}
            onChange={(e) => table.setPageSize(Number(e.target.value))}
            style={{ border: "1px solid #E5E7EB", borderRadius: 6, padding: "4px 6px" }}
          >
            {[10, 25, 50, 100].map((ps) => <option key={ps} value={ps}>Show {ps}</option>)}
          </select>
          <button onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>Next ›</button>
          <button onClick={() => table.lastPage()} disabled={!table.getCanNextPage()}>Last »</button>
        </div>
      </div>

      <hr style={{ margin: "20px 0" }} />

      {/* Modals */}
      <AddModal
        open={showAdd}
        onClose={() => setShowAdd(false)}
        onSuccess={() => { setShowAdd(false); fetchData(); }}
      />
      <EditModal
        row={editingRow}
        onClose={() => setEditingRow(null)}
        onSaved={() => { setEditingRow(null); fetchData(); }}
      />
      <DeleteModal
        row={deletingRow}
        onClose={() => setDeletingRow(null)}
        onDeleted={() => { setDeletingRow(null); fetchData(); }}
      />
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────
   Styles
────────────────────────────────────────────────────────────── */
function inputStyle() {
  return { background: "#ffff", color: "black", border: `1px solid ${UI.purple}`, padding: "8px 14px", borderRadius: 10, fontWeight: 100, width: "100%" };
}
function selectStyle() {
  return { background: "#ffff", color: "black", border: `1px solid ${UI.purple}`, padding: "8px 14px", borderRadius: 10, fontWeight: 100, width: "100%", cursor: "pointer" };
}
function primaryBtnStyle(disabled) {
  return { background: UI.purple, color: "white", padding: "8px 16px", border: "1px solid " + UI.indigo600, borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer", boxShadow: "0 1px 2px rgba(0,0,0,0.1)", transition: "background 0.2s ease-in-out" };
}
function ghostBtnStyle() {
  return { background: "transparent", color: "#374151", border: `1px solid ${UI.indigo600}`, padding: "8px 14px", borderRadius: 10, fontWeight: 600, cursor: "pointer" };
}
function dangerBtnStyle() {
  return { background: "#FEF2F2", color: "#B91C1C", border: "1px solid #FCA5A5", padding: "6px 10px", borderRadius: 6, cursor: "pointer" };
}
function chipBtnStyle() {
  return { background: "#F3F4F6", color: UI.slate700, border: "1px solid #E5E7EB", padding: "6px 10px", borderRadius: 8, cursor: "pointer", fontWeight: 600 };
}
function iconBtnStyle() {
  return { display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 10px", borderRadius: 6, border: "1px solid #E5E7EB", background: "#fff", cursor: "pointer" };
}
